import classNames from 'classnames';
import { JSX, useState } from 'react';
import { styled } from '@mui/material/styles';
import { mainFrameRefContext } from './context';
import { Footer } from './components/Footer';
import { ReactComponent as AvatarBase } from '@/assets/avatar_base.svg';
import { ReactComponent as BackgroundPattern } from '@/assets/pattern.svg';
import { HeaderActions } from 'src/components/UI/Frame/components/HeaderActions';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { useSelector } from 'react-redux';
import { getWave } from '../../../reduxStore/selectors/messages.selector';
import { ReactComponent as WaveIcon } from '@/assets/wave.svg';
import { scrollbar } from 'src/theme/mixins';

type IHeaderText = {
	title: string;
	description?: string;
	logo?: string;
};

export const Frame = styled(
	({
		className,
		children,
		header: { title, description, logo },
		body,
		footer,
		wave: wavePosition,
		...props
	}: Omit<JSX.IntrinsicElements['section'], 'children'> & {
		children?: never;
		header: IHeaderText;
		body: React.ReactNode;
		footer: React.ReactNode;
		wave?: 'right' | 'left';
	}) => {
		const [_ref, setRef] = useState<HTMLElement | null>(null);
		const wave = useSelector(getWave);
		const { isMobile } = useBreakpoint();
		return (
			<section
				ref={setRef}
				className={classNames('chat__frame', className)}
				{...props}
			>
				<mainFrameRefContext.Provider value={_ref}>
					<header
						className={classNames(
							'chat__header',
							'Frame-header',
							isMobile && wave === 'right' && 'header--slim',
						)}
					>
						<section className={classNames('header__body', 'Frame-headerBody')}>
							<aside>
								<figure className="header__logo">
									{logo ? <img src={logo} alt="" /> : <AvatarBase />}
								</figure>
							</aside>
							<main>
								<h5 className="header__title">{title}</h5>
								{description ? (
									<span className="header__description">{description}</span>
								) : null}
							</main>
						</section>
						<HeaderActions isMobile={isMobile} />
					</header>
					<div className={classNames('chat__body', 'Frame-body')}>
						<BackgroundPattern className="pattern" />
						<WaveIcon
							className={classNames('wave', `wave--${wavePosition ?? wave}`)}
							preserveAspectRatio="none"
						/>
						<main className={classNames('chat__main', 'Frame-main')}>
							{body}
						</main>
					</div>
					<Footer>{footer}</Footer>
				</mainFrameRefContext.Provider>
			</section>
		);
	},
	{ label: 'Frame', target: 'Frame-root' },
)({
	flex: 1,
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	position: 'relative',
	pointerEvents: 'all',
	backgroundColor: 'var(--chat__frame_background)',
	'& .Frame-header': {
		backgroundColor: 'var(--chat__header_background)',
		zIndex: 11,
		display: 'flex',
		width: '100%',
	},
	'& .Frame-headerBody': {
		margin: '15px',
		display: 'flex',
		alignItems: 'center',
		fontSize: '0.87em',
		'& > main': {
			flex: 1,
			display: 'flex',
			flexDirection: 'column',
		},
		'& .header__logo': {
			margin: 0,
			marginRight: '16px',
			position: 'relative',
			'& img': {
				height: 'auto',
				maxHeight: '50px',
				maxWidth: 'calc(50px * 2)',
			},
			'& svg': {
				opacity: 'var(--chat__header__logo_opacity, 1)',
				transform: 'scaleX(-1)',
				height: '50px',
				width: '50px',
				'& .st0': {
					fill: 'var(--chat__header__logo_color)',
				},
			},
		},
		'& .header__title': {
			fontSize: '2em',
			fontFamily: 'Ubuntu, sans-serif',
			fontWeight: 400,
			color: 'var(--chat__header__title_color)',
			margin: 0,
		},
		'& .header__description': {
			fontSize: '1.5em',
			fontFamily: 'Roboto, sans-serif',
			fontWeight: 400,
			color: 'var(--chat__header__description_color)',
			margin: 0,
		},
	},
	'& .Frame-body': {
		flex: 1,
		flexBasis: '60%',
		position: 'relative',
		display: 'flex',
		overflow: 'hidden',
		flexDirection: 'column',
		marginTop: '-1px',
		backgroundColor: 'var(--chat__body_background)',
		'& .pattern': {
			pointerEvents: 'none',
			position: 'absolute',
			top: 0,
			left: 0,
			height: '100%',
			width: '100%',
			bottom: 0,
			right: 0,
			fontSize: '0.2px',
			opacity: 'var(--pattern_opacity)',
			'--color': 'var(--pattern_color)',
			zIndex: 0,
		},
		'& .wave': {
			width: '200%',
			height: 'auto',
			maxHeight: '46px',
			top: 'calc(var(--chat__wave_border_width) * -0.5 - 1px)',
			position: 'absolute',
			zIndex: 11,
			transition: '0.5s',
			transitionDelay: '0.3s',
			transitionTimingFunction: 'ease-in-out',
			transitionProperty: 'left',
			pointerEvents: 'none',
			'& .fill': {
				fill: 'var(--chat__wave_color)',
				stroke: 'none',
			},
			'& .stroke': {
				stroke: 'var(--chat__wave_border)',
				strokeWidth: 'var(--chat__wave_border_width)',
				fill: 'none',
			},
			left: 'calc(-1px - var(--chat__wave_border_width))',
			'&.wave--right': {
				left: '-100%',
			},
		},
	},
	'& .Frame-main': {
		padding: '20px',
		paddingBottom: 0,
		zIndex: 1,
		...scrollbar(),
	},
});
